import { useParams, useRouter } from "@tanstack/react-router";
import {
  useNullableSuspenseGqlQuery,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { globalSupplierSelectionList } from "@/app/Suppliers/GraphQL/globalSupplierSelectionList";
import { supplierRecordQuery } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import type { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { buildScopeChangePath } from "@/app/Organisations/Utils/buildScopeChangePath";
import { type Tenant } from "@/app/Organisations/Utils/tenant";
import {
  supplierParamToId,
  SUPPLIER_LIST_VARIABLES,
} from "@/app/Suppliers/Utils/globalSupplier";

/**
 * Active supplier, driven entirely by the URL
 * (`/$tenant/$orgId/supplier/{-$supplierId}`; absent segment = all suppliers).
 * No provider needed — list + record come from the cache the app-layout
 * loader already warmed.
 */
export function useGlobalSupplier() {
  const router = useRouter();
  const {
    tenant,
    orgId,
    supplierId: param,
  } = useParams({ strict: false }) as {
    tenant?: string;
    orgId?: string;
    supplierId?: string;
  };
  const supplierId = supplierParamToId(param);

  const { data } = useSuspenseGqlQuery(
    globalSupplierSelectionList,
    SUPPLIER_LIST_VARIABLES,
  );

  const { data: record } = useNullableSuspenseGqlQuery(
    supplierRecordQuery,
    { id: supplierId ?? "" },
    !!supplierId,
  );

  function setSupplier(nextSupplierId: string | null) {
    // Push the choice into the URL (the source of truth). Stays on the current
    // page, except a record screen drops to its list, since the record is
    // unlikely to be visible under the new supplier.
    const to = buildScopeChangePath(router, {
      tenant: (tenant as Tenant | undefined) ?? "org",
      orgId: orgId ?? "",
      supplierId: nextSupplierId,
    });
    return router.navigate({ to });
  }

  // A user managing a single supplier has nothing to choose between, so
  // "all suppliers" still resolves to that one for forms that need it.
  const soleSupplierId =
    data.suppliers.paginatorInfo.total === 1
      ? (data.suppliers.data[0]?.id ?? null)
      : null;

  return {
    supplierId,
    defaultSupplierId: supplierId ?? soleSupplierId,
    hasSupplier: !!supplierId,
    supplierList: data.suppliers.data ?? [],
    supplierCount: data.suppliers.paginatorInfo.total,
    actions: { setSupplier },
    supplier: (record?.supplier ?? undefined) as SupplierRecord,
  };
}
